import { debounce, isEmpty, isEqual } from 'lodash';
import { deleteDataState, getDataState, upsertDataState } from './dbState';
import { addDataHistoryState } from './dbHistoryState';
import { getIsUndoRedo, getPositionActive, setIsUndoRedo } from './undoRedoState';

/**
 * Logs a failed fire-and-forget persistence write instead of leaving it as an unhandled rejection. Used to `.catch` the debounced IndexedDB writes, which are intentionally not awaited.
 *
 * @param error - The rejection reason from the persistence write.
 */
const logPersistError = (error: unknown) => {
  console.error('[reportPersistence] persist write failed', error);
};

/**
 * Reads a single persisted report field out of the state database. Unwraps the stored record and yields undefined when nothing is saved under the key.
 *
 * @param key - Persistence key of the field to read.
 * @returns A promise resolving to the persisted value, or undefined when the key is absent.
 */
export const loadPersistedValue = async <T>(key: string): Promise<T | undefined> => {
  const saved = (await getDataState(key)) as { value: T } | undefined;
  return saved?.value;
};

/**
 * Loads one persisted value per tab key and assembles them into a keyed record. Reads every composed baseKey and tabKey entry in parallel and skips the tab keys whose stored value is undefined.
 *
 * @param baseKey - Field key the tab key is appended to.
 * @param tabKeys - Tab keys whose persisted values are loaded.
 * @returns A promise resolving to a record mapping each tab key that had a stored value to that value.
 */
export const loadPersistedKeyedRecord = async <T>(
  baseKey: string,
  tabKeys: readonly string[],
): Promise<Record<string, T>> => {
  const entries = await Promise.all(
    tabKeys.map(
      async (tabKey) => [tabKey, await loadPersistedValue<T>(`${baseKey}-${tabKey}`)] as const,
    ),
  );
  const record: Record<string, T> = {};
  entries.forEach(([tabKey, value]) => {
    if (value !== undefined) record[tabKey] = value;
  });
  return record;
};

type PersistFn = (newValue: unknown, oldValue: unknown) => void;

/**
 * `getIsUndoRedo()` is read synchronously here, at call time (i.e. when the
 * field is actually set), not inside the debounced `flush` 300ms later —
 * matching v1, where `isUndoRedo` was captured as a plain argument to
 * `debounceOnSet` at `onSet` time. A single undo/redo restore can set many
 * fields synchronously (e.g. `beatOptionsState`'s undo case sets 10 fields);
 * each field's own debounced flush unconditionally clears the shared flag at
 * the end, so reading it live 300ms later risks a sibling field's flush
 * having already cleared it first. Capturing it now gives each field its own
 * frozen snapshot, immune to what order the other fields' flushes complete in.
 */
/**
 * Builds the persist callback that saves one report field and optionally records it for undo and redo. Debounces the write by 300ms, upserting the new value when it differs, deleting the entry when both values are empty and equal, and appending an undo history entry stamped with the active tab positions unless the change itself came from an undo or redo.
 *
 * @param key - Persistence key the field is stored under.
 * @param opts - Options carrying recordUndo, which enables undo history recording.
 * @returns A persist function taking the new and old values and scheduling the debounced write.
 */
export const createFieldPersistence = (
  key: string,
  opts: { recordUndo?: boolean } = {},
): PersistFn => {
  const flush = debounce(async (newValue: unknown, oldValue: unknown, wasUndoRedo: boolean) => {
    const equal = isEqual(newValue, oldValue);
    if (!equal) {
      Promise.resolve(upsertDataState(key, newValue)).catch(logPersistError);
    }
    if (isEmpty(newValue) && isEmpty(oldValue) && equal) {
      Promise.resolve(deleteDataState(key)).catch(logPersistError);
    }
    if (opts.recordUndo && !wasUndoRedo) {
      try {
        const positions = await getPositionActive();
        Promise.resolve(addDataHistoryState(key, { newValue, oldValue, positions } as any)).catch(
          logPersistError,
        );
      } catch (error) {
        logPersistError(error);
      }
    }
    setIsUndoRedo(false);
  }, 300);
  return (newValue, oldValue) => {
    flush(newValue, oldValue, getIsUndoRedo());
  };
};

const keyedCache = new Map<string, PersistFn>();

/**
 * Returns the per-tab persist callback for a field, memoized so each tab reuses one debounced writer. Keys the cache by base key, tab key and whether undo recording is on, creating the underlying field persistence on first use.
 *
 * @param baseKey - Field key shared across tabs.
 * @param tabKey - Tab this persist callback is scoped to.
 * @param opts - Options carrying recordUndo, which enables undo history recording.
 * @returns The cached persist function for that base key and tab key.
 */
export const createKeyedFieldPersistence = (
  baseKey: string,
  tabKey: string,
  opts: { recordUndo?: boolean } = {},
): PersistFn => {
  const cacheKey = `${baseKey}-${tabKey}-${opts.recordUndo ? 'undo' : 'plain'}`;
  if (!keyedCache.has(cacheKey)) {
    keyedCache.set(cacheKey, createFieldPersistence(`${baseKey}-${tabKey}`, opts));
  }
  return keyedCache.get(cacheKey)!;
};
